/**
 * Custom workflow handler registry.
 *
 * Provides a singleton registry for registering and retrieving custom
 * workflow handlers that are referenced by string key in form configurations.
 * Modules register transforms, success/error/cancel handlers, and custom
 * action handlers at startup so the EntityForm can resolve them at runtime.
 *
 * @see spec section 7.6
 */

import type { UseEntityFormReturn } from '../types';

// ---------------------------------------------------------------------------
// Handler type aliases
// ---------------------------------------------------------------------------

/** Transforms a payload before submission. */
export type PayloadTransformFn = (
  payload: Record<string, unknown>,
) => Record<string, unknown>;

/** Handles a successful API response. */
export type SuccessHandlerFn = (
  response: unknown,
  formState: UseEntityFormReturn,
) => void;

/** Handles an API error response. */
export type ErrorHandlerFn = (
  error: unknown,
  formState: UseEntityFormReturn,
) => void;

/** Handles a cancel action. */
export type CancelHandlerFn = (formState: UseEntityFormReturn) => void;

/** Handles a custom action button click. */
export type ActionHandlerFn = (
  formState: UseEntityFormReturn,
) => void | Promise<void>;

// ---------------------------------------------------------------------------
// Registry interface
// ---------------------------------------------------------------------------

export interface WorkflowRegistry {
  /** Register a payload transform function. */
  registerTransform(key: string, fn: PayloadTransformFn): void;

  /** Register a custom success handler. */
  registerSuccessHandler(key: string, fn: SuccessHandlerFn): void;

  /** Register a custom error handler. */
  registerErrorHandler(key: string, fn: ErrorHandlerFn): void;

  /** Register a custom cancel handler. */
  registerCancelHandler(key: string, fn: CancelHandlerFn): void;

  /** Register a custom action handler (for custom buttons). */
  registerActionHandler(key: string, fn: ActionHandlerFn): void;

  /** Get a registered transform. Returns `undefined` if not found. */
  getTransform(key: string): PayloadTransformFn | undefined;

  /** Get a registered success handler. Returns `undefined` if not found. */
  getSuccessHandler(key: string): SuccessHandlerFn | undefined;

  /** Get a registered error handler. Returns `undefined` if not found. */
  getErrorHandler(key: string): ErrorHandlerFn | undefined;

  /** Get a registered cancel handler. Returns `undefined` if not found. */
  getCancelHandler(key: string): CancelHandlerFn | undefined;

  /** Get a registered action handler. Returns `undefined` if not found. */
  getActionHandler(key: string): ActionHandlerFn | undefined;
}

// ---------------------------------------------------------------------------
// Internal storage & factory
// ---------------------------------------------------------------------------

function createWorkflowRegistry(): WorkflowRegistry {
  const transforms = new Map<string, PayloadTransformFn>();
  const successHandlers = new Map<string, SuccessHandlerFn>();
  const errorHandlers = new Map<string, ErrorHandlerFn>();
  const cancelHandlers = new Map<string, CancelHandlerFn>();
  const actionHandlers = new Map<string, ActionHandlerFn>();

  return {
    // -- Registration -------------------------------------------------------

    registerTransform(key, fn) {
      transforms.set(key, fn);
    },

    registerSuccessHandler(key, fn) {
      successHandlers.set(key, fn);
    },

    registerErrorHandler(key, fn) {
      errorHandlers.set(key, fn);
    },

    registerCancelHandler(key, fn) {
      cancelHandlers.set(key, fn);
    },

    registerActionHandler(key, fn) {
      actionHandlers.set(key, fn);
    },

    // -- Retrieval -----------------------------------------------------------

    getTransform(key) {
      return transforms.get(key);
    },

    getSuccessHandler(key) {
      return successHandlers.get(key);
    },

    getErrorHandler(key) {
      return errorHandlers.get(key);
    },

    getCancelHandler(key) {
      return cancelHandlers.get(key);
    },

    getActionHandler(key) {
      return actionHandlers.get(key);
    },
  };
}

// ---------------------------------------------------------------------------
// Singleton export
// ---------------------------------------------------------------------------

/**
 * Singleton workflow handler registry.
 *
 * Usage:
 * ```ts
 * import { workflowRegistry } from '@components/integrated/entity-form/workflow/registry';
 *
 * workflowRegistry.registerTransform('addTimestamp', (payload) => ({
 *   ...payload,
 *   updatedAt: new Date().toISOString(),
 * }));
 * ```
 */
export const workflowRegistry: WorkflowRegistry = createWorkflowRegistry();
